import { Injectable, inject } from '@angular/core';
import { productSummaryApiActions, productSummaryPageActions } from '@mkp/product-summary/actions';
import { selectSelectedAccountId } from '@mkp/account/state';
import { HomePageActions } from '@mkp/core/feature-home/actions';
import { PurchaseCreditsPageActions } from '@mkp/credit/feature-purchase-credits/actions';
import { ProductOfferingActions } from '@mkp/product/feature-product-offering/actions';
import { ActionState, LOCAL_STORAGE_VOUCHER_KEY } from '@mkp/shared/models';
import { getMessageError, ProductResource } from '@mkp/shared/data-access';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { productOptionsApiActions, productOptionsPageActions } from '@mkp/product-options/actions';
import { selectProducts } from './product-options.selectors';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class ProductOptionsEffects {
  private readonly actions$ = inject(Actions);

  // remove coupon and force-refresh product-options on checkout
  readonly loadProductOptionsPostCheckout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(productSummaryApiActions.productCheckoutSuccess),
      tap(({ cart }) => {
        if (cart.promotionCoupon?.code === localStorage.getItem(LOCAL_STORAGE_VOUCHER_KEY)) {
          localStorage.removeItem(LOCAL_STORAGE_VOUCHER_KEY);
        }
      }),
      this.fetchProductListOperator()
    );
  });

  private readonly store = inject(Store);

  // load product-options, only if there is NOT already a value in store
  readonly loadProductOptionsIfNotLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        productOptionsPageActions.enter,
        PurchaseCreditsPageActions.enter,
        productSummaryPageActions.enter,
        ProductOfferingActions.enter,
        HomePageActions.enter
      ),
      concatLatestFrom(() => this.store.select(selectProducts).pipe(map(Boolean))),
      filter(([, hasProducts]) => !hasProducts),
      map(([action]) => action),
      this.fetchProductListOperator()
    );
  });

  private readonly snackBarNotificationsService = inject(SnackbarService);

  readonly loadProductOptionsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(productOptionsApiActions.loadProductOptionsFailure),
        tap(() => {
          this.snackBarNotificationsService.show('PRODUCT_OPTIONS.QUERY_ERROR_MSG', {
            state: ActionState.Error,
          });
        })
      );
    },
    { dispatch: false }
  );

  private readonly productResource = inject(ProductResource);

  private fetchProductListOperator<T>(): (source: Observable<T>) => Observable<Action> {
    return (source) =>
      source.pipe(
        concatLatestFrom(() => this.store.select(selectSelectedAccountId)),
        switchMap(([, selectedAccountId]) =>
          this.productResource.getAll(selectedAccountId).pipe(
            map((products) => productOptionsApiActions.loadProductOptionsSuccess({ products })),
            catchError((error: unknown) =>
              of(
                productOptionsApiActions.loadProductOptionsFailure({
                  error: getMessageError(error, 'ProductOptionsEffects'),
                })
              )
            )
          )
        )
      );
  }
}
